import { useEffect, useMemo, useRef, useState } from "react";
import { Snackbar } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types.ts";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import {
  getPIClubs,
  getPorts,
  getVesselClassificationSocieties,
  getVesselTypes,
} from "@services/referenceDataApi";
import { type VesselsResponse } from "@services/vessel/types";
import { getSearchVessels } from "@services/vesselsApi";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";

export const LANG_BASE = "vessel-management";
export const LANG_BASE_HEADER = `${LANG_BASE}.tableHeaders`;
const TEST_ID_BASE = "registered-vessels";

const LOCAL_STORAGE_PAGE_KEY = "registered-vessels-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "registered-vessels-page-size";
const LOCAL_STORAGE_FILTERS_KEY = "registered-vessels-filters";
const SEARCH_INQUIRY_ID = "vessel-management.registered-vessels.search";
const SEARCH_INQUIRY_CONTEXT = {
  module: "vessel_registration",
  screen: "Registered Vessels",
  action: "search_registered_vessels",
  entityType: "vessel",
};

export interface PaginationParams {
  pageSize: number;
  page: number;
}

export const RegisteredVessels = () => {
  const { t } = useTranslation();
  const { registerVesselLabelKey, userAccessPermissions } = useAuthStore();

  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : 25;
  });

  const isMarineDepartment = checkPermission(userAccessPermissions, "hasChangeVesselStatusAccess");

  const mapStatus = (status: string): string =>
    t(`${LANG_BASE}.status-labels.${status}`, {
      defaultValue: t("common.unknown"),
    });

  const mapRestrictions = (restriction: string): string =>
    t(`${LANG_BASE}.restrictionsLabels.${restriction}`, {
      defaultValue: t("common.unknown"),
    });

  const headers: Header[] = useMemo(
    () => [
      { field: "imoNo", columnName: t(`${LANG_BASE_HEADER}.imoNo.`) },
      { field: "vesselName", columnName: t(`${LANG_BASE_HEADER}.vesselName`) },
      { field: "vesselType", columnName: t(`${LANG_BASE_HEADER}.vesselType`) },
      { field: "loa", columnName: t(`${LANG_BASE_HEADER}.loa`) },
      { field: "portOfRegistry", columnName: t(`${LANG_BASE_HEADER}.portOfRegistry`) },
      { field: "grt", columnName: t(`${LANG_BASE_HEADER}.grt`) },
      {
        field: "classificationSociety",
        columnName: t(`${LANG_BASE_HEADER}.classificationSociety`),
      },
      { field: "piAndIClub", columnName: t(`${LANG_BASE_HEADER}.pAndIClub`) },
      { field: "status", columnName: t(`${LANG_BASE_HEADER}.status`) },
      ...(isMarineDepartment
        ? [{ field: "restrictions", columnName: t(`${LANG_BASE_HEADER}.restrictions`) }]
        : []),
    ],
    [t, isMarineDepartment],
  );
  const service = "bff-vessel";
  const {
    data: vesselsData,
    isLoading: isVesselsIsLoading,
    isFetching: isVesselsFetching,
    isError: isVesselsError,
    isSuccess: isVesselsSuccess,
    dataUpdatedAt: vesselsDataUpdatedAt,
    error,
  } = useQuery<VesselsResponse>({
    queryKey: ["registeredVessels", page, pageSize, filters],
    queryFn: () => getSearchVessels(service, { page, pageSize }, filters),
  });
  const { vessels = [], totalRecordCount = 0 } = vesselsData ?? {};

  useTrackPendingInquiry(SEARCH_INQUIRY_ID, {
    isFetching: isVesselsFetching,
    isLoading: isVesselsIsLoading,
    isError: isVesselsError,
    isSuccess: isVesselsSuccess,
    error,
    dataUpdatedAt: vesselsDataUpdatedAt,
  });

  const { data: vesselTypes } = useQuery({
    queryKey: ["vesselTypes"],
    queryFn: () => getVesselTypes(service),
  });

  const { data: ports } = useQuery({
    queryKey: ["ports"],
    queryFn: () => getPorts(service),
  });

  const { data: vesselClassificationSocieties } = useQuery({
    queryKey: ["vesselClassificationSocieties"],
    queryFn: () => getVesselClassificationSocieties(service),
  });

  const { data: piClubs } = useQuery({
    queryKey: ["piClubs"],
    queryFn: () => getPIClubs(service),
  });

  const filterFields: Record<string, FilterFieldDefinition> = useMemo(
    () => ({
      imoNumbers: {
        label: t(`${LANG_BASE_HEADER}.imoNo.`),
        type: "integer",
        minLength: 3,
      },
      vesselNames: {
        label: t(`${LANG_BASE_HEADER}.vesselName`),
        type: "text",
        minLength: 3,
      },
      vesselTypeCodes: {
        label: t(`${LANG_BASE_HEADER}.vesselType`),
        type: "autocomplete",
        options:
          vesselTypes
            ?.filter(({ vesselTypeCode }) => vesselTypeCode)
            .map(({ vesselTypeCode, vesselTypeDesc }) => ({
              label: vesselTypeDesc!.toUpperCase(),
              value: vesselTypeCode!,
              key: vesselTypeCode!,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
      },
      loa: {
        label: t(`${LANG_BASE_HEADER}.loa`),
        type: "number",
        maxLength: 15,
        maxDigits: 3,
      },
      portCodes: {
        label: t(`${LANG_BASE_HEADER}.portOfRegistry`),
        type: "autocomplete",
        options:
          ports
            ?.filter(({ portName }) => portName)
            .sort((a, b) => {
              const nameCompare = a.portName!.localeCompare(b.portName!);
              if (nameCompare) return nameCompare;
              // If portName is the same, sort by portCode
              return (a.portCode ?? "").localeCompare(b.portCode ?? "");
            })
            .map(({ portName, portCode }) => ({
              label: `${portName!} (${portCode})`,
              value: portCode!,
              key: portCode!,
            })) ?? [],
      },
      grt: {
        label: t(`${LANG_BASE_HEADER}.grt`),
        type: "number",
        maxLength: 15,
        maxDigits: 3,
      },
      classificationSocietyCodes: {
        label: t(`${LANG_BASE_HEADER}.classificationSociety`),
        type: "autocomplete",
        options:
          vesselClassificationSocieties
            ?.filter(
              ({ vesselClassificationSocietyCode, vesselClassificationSocietyDesc }) =>
                vesselClassificationSocietyCode && vesselClassificationSocietyDesc,
            )
            .sort((a, b) =>
              a.vesselClassificationSocietyDesc!.localeCompare(b.vesselClassificationSocietyDesc!),
            )
            .map(({ vesselClassificationSocietyCode, vesselClassificationSocietyDesc }) => ({
              label: `${vesselClassificationSocietyCode} - ${vesselClassificationSocietyDesc?.trim()}`,
              value: vesselClassificationSocietyCode!,
              key: vesselClassificationSocietyCode!,
            })) ?? [],
      },
      piClubCodes: {
        label: t(`${LANG_BASE_HEADER}.pAndIClub`),
        type: "autocomplete",
        options:
          piClubs
            ?.filter(({ piClubCode, piClubName }) => piClubCode && piClubName)
            .sort((a, b) => a.piClubName!.localeCompare(b.piClubName!))
            .map(({ piClubCode, piClubName }) => ({
              label: piClubName!,
              value: piClubCode!,
              key: piClubCode!,
            })) ?? [],
      },
      statuses: {
        label: t(`${LANG_BASE_HEADER}.status`),
        type: "autocomplete",
        options: [
          { label: "Approved", value: "APPROVED" },
          { label: "Cancelled", value: "CANCELLED" },
          ...(isMarineDepartment ? [] : [{ label: "Draft", value: "DRAFT" }]),
          { label: "Pending Approval", value: "PENDING_APPROVAL" },
          { label: "Rejected", value: "REJECTED" },
        ],
      },
    }),
    [vesselTypes, ports, vesselClassificationSocieties, t, isMarineDepartment],
  );

  const onApplyFilters = (filters: FilterItem[]) => {
    startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    setFilters(filters);
    localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(filters));
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  const onClearFilters = () => {
    if (filters.length) {
      startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    }
    setFilters([]);
    localStorage.removeItem(LOCAL_STORAGE_FILTERS_KEY);
  };

  const formattedFormData = vessels.map(
    ({
      id,
      vesselImoNumber,
      vesselName,
      vesselType,
      loa,
      port,
      grt,
      vesselClassificationSociety,
      otherVesselClassificationSociety,
      shipOwnership,
      task,
      vesselRegistrationStatus,
      vesselLogType,
    }) => ({
      id,
      imoNo: vesselImoNumber,
      vesselName: vesselName,
      vesselType: vesselType?.vesselTypeDesc?.toUpperCase(),
      loa: loa,
      portOfRegistry: port?.portName,
      grt: grt,
      classificationSociety: otherVesselClassificationSociety
        ? otherVesselClassificationSociety
        : vesselClassificationSociety?.vesselClassificationSocietyCode
          ? `${vesselClassificationSociety.vesselClassificationSocietyCode} - ${vesselClassificationSociety.vesselClassificationSocietyDesc?.trim()}`
          : undefined,
      piAndIClub: shipOwnership?.piClub?.piClubName,
      task,
      status: mapStatus(vesselRegistrationStatus ?? ""),
      ...(isMarineDepartment && {
        restrictions: mapRestrictions(vesselLogType?.vesselLogTypeCode ?? ""),
      }),
    }),
  );

  // AppInsights: Track initial screen load time

  const startedAt = useRef(performance.now());
  const hasVesselsTrackedInitialLoad = useRef(false);

  useEffect(() => {
    if (isVesselsIsLoading || error || hasVesselsTrackedInitialLoad.current) {
      return;
    }

    hasVesselsTrackedInitialLoad.current = true;

    completeScreenNavigation("Vessel Management");
    trackScreenLoad("Vessel Management", "Vessel Management", startedAt.current);
  }, [isVesselsIsLoading, error]);

  if (error) {
    return <Snackbar open autoHideDuration={6000} message={error.message} />;
  }

  if (isVesselsIsLoading) {
    return <LoadingOverlay loading={isVesselsIsLoading} />;
  }

  return (
    <div data-testid={TEST_ID_BASE}>
      {!isVesselsIsLoading && (
        <CustomDataTable
          testId={TEST_ID_BASE}
          title={t(`${LANG_BASE}.registeredVessels`)}
          actionName={t(registerVesselLabelKey)}
          headers={headers}
          rows={formattedFormData}
          page={page}
          pageSize={pageSize}
          totalCount={totalRecordCount}
          canPreview={true}
          onPageChange={(newPage, newSize) => {
            if (newPage !== page || newSize !== pageSize) {
              startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
            }
            setPage(newPage);
            setPageSize(newSize);
            localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
            localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
          }}
          navigationLinkBase="/vessel-management/vessel-status"
          pageSizeOptions={[5, 10, 15, 20, 25]}
          filters={filters}
          onApplyFilters={onApplyFilters}
          onClearFilters={onClearFilters}
          availableFields={filterFields}
          shouldFreezeExtraColumn={isMarineDepartment}
        />
      )}
    </div>
  );
};

export default RegisteredVessels;
